import { NotePencil, Plus, Trash } from '@phosphor-icons/react'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useOutletContext } from 'react-router-dom'
import type { DashboardOutletContext } from '../components/AppShell'
import { Button, CategoryBadge, ConflictDialog, Drawer, Field, Panel, StatusBadge } from '../components/common'
import { api, conflictCurrent } from '../lib/api'
import { takeoverCategories, takeoverStatuses, workingItemOwners } from '../lib/constants'
import type { Summary, TakeoverItem } from '../lib/types'

type TakeoverForm = Pick<TakeoverItem, 'category' | 'item' | 'owner' | 'fii' | 'platform' | 'total' | 'tested' | 'independent' | 'independentNew' | 'documentation' | 'confidence' | 'status' | 'note'>

const emptyForm: TakeoverForm = {
  category: 'ESS', item: '', owner: '', fii: '', platform: '', total: null, tested: null,
  independent: null, independentNew: null, documentation: null, confidence: null,
  status: '尚未開始', note: '',
}

const labels: Record<string, string> = {
  category: '類別', item: '承接項目', owner: 'Dell Owner', fii: 'Fii Owner', platform: 'Project', total: '總數', tested: '已測試',
  independent: '獨立完成率', independentNew: '新平台獨立完成率', documentation: '文件完成率', confidence: '信心度', status: '狀態', note: '備註',
}

export function TakeoverPage() {
  const { data, refresh, canEdit, isAdmin } = useOutletContext<DashboardOutletContext>()
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('全部')
  const [status, setStatus] = useState('全部')
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null)
  const [selectedOwner, setSelectedOwner] = useState<string | null>(null)
  const [editing, setEditing] = useState<TakeoverItem | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [form, setForm] = useState<TakeoverForm>(emptyForm)
  const [summaryOpen, setSummaryOpen] = useState(false)
  const [summary, setSummary] = useState(data.summary)
  const [conflict, setConflict] = useState<TakeoverItem | null>(null)
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (!message) return
    const timer = setTimeout(() => setMessage(''), 2500)
    return () => clearTimeout(timer)
  }, [message])

  const filtered = useMemo(() => data.takeover.filter((row) => {
    const text = `${row.item} ${row.owner} ${row.fii} ${row.platform} ${row.note}`.toLowerCase()
    const matchCategory = category === '全部' || row.category === category
    const matchStatus = status === '全部' || row.status === status
    const matchPlatform = !selectedPlatform || (row.platform || '未指定') === selectedPlatform
    const matchOwner = !selectedOwner || parseOwners(`${row.owner}, ${row.fii}`).includes(selectedOwner)
    const matchQuery = text.includes(query.toLowerCase())
    return matchCategory && matchStatus && matchPlatform && matchOwner && matchQuery
  }), [category, data.takeover, query, selectedOwner, selectedPlatform, status])

  const save = useMutation({
    mutationFn: () => editing
      ? api.updateEntity<TakeoverItem>('takeover', editing.id, editing.version, form)
      : api.createEntity<TakeoverItem>('takeover', form),
    onSuccess: async () => { closeDrawer(); setMessage('資料已儲存。'); await refresh() },
    onError: (error) => { const current = conflictCurrent<TakeoverItem>(error); current ? setConflict(current) : setMessage(error instanceof Error ? error.message : '儲存失敗。') },
  })

  const saveSummary = useMutation({
    mutationFn: () => api.updateSummary(data.summary.version, summaryValues(summary)),
    onSuccess: async () => { setSummaryOpen(false); setMessage('摘要已更新。'); await refresh() },
    onError: (error) => setMessage(error instanceof Error ? error.message : '摘要更新失敗。'),
  })

  const openNew = () => { setEditing(null); setForm(emptyForm); setConflict(null); setDrawerOpen(true) }
  const openEdit = (row: TakeoverItem) => { setEditing(row); setForm(toForm(row)); setConflict(null); setDrawerOpen(true) }
  const closeDrawer = () => { setDrawerOpen(false); setEditing(null); setForm(emptyForm); setConflict(null) }

  const remove = async (row: TakeoverItem) => {
    if (!window.confirm(`確定刪除「${row.item}」？資料仍會保留在稽核紀錄中。`)) return
    try { await api.deleteEntity('takeover', row.id, row.version); await refresh() } catch (error) { setMessage(error instanceof Error ? error.message : '刪除失敗。') }
  }

  return (
    <section className="page-stack">
      <div className="working-header-bar">
        <div className="working-header-title-group">
          <h1>承接能力盤點</h1>
          <div className="working-stat-pills">
            <span className="stat-pill blue">掌握測試 <strong>{data.summary.coreItemsCurrent}/{data.summary.coreItemsTotal}</strong></span>
            <span className="stat-pill">人力 <strong>{data.summary.essPeople} 人</strong></span>
          </div>
        </div>
        {canEdit ? (
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <Button onClick={() => { setSummary(data.summary); setSummaryOpen(true) }}>
              <NotePencil size={15} />編輯摘要
            </Button>
            <Button variant="primary" onClick={openNew}>
              <Plus size={15} weight="bold" />新增項目
            </Button>
          </div>
        ) : null}
      </div>
      {message ? (
        <div className="floating-toast">
          <span>{message}</span>
          <button type="button" onClick={() => setMessage('')} aria-label="關閉通知">×</button>
        </div>
      ) : null}
      <Panel>
        <div className="panel-toolbar">
          <div>
            <h2>承接項目明細</h2>
          </div>
          <div className="filters">
            <input className="search-input" placeholder="搜尋項目、Owner、Project 或備註" value={query} onChange={(e) => setQuery(e.target.value)} />
            {category !== '全部' ? (
              <div className="owner-filter-chip">
                <span>類別: <strong>{category}</strong></span>
                <button type="button" onClick={() => setCategory('全部')} aria-label="清除類別篩選">×</button>
              </div>
            ) : null}
            {status !== '全部' ? (
              <div className="owner-filter-chip">
                <span>狀態: <strong>{status}</strong></span>
                <button type="button" onClick={() => setStatus('全部')} aria-label="清除狀態篩選">×</button>
              </div>
            ) : null}
            {selectedPlatform ? (
              <div className="owner-filter-chip project-filter-chip">
                <span>Project: <strong>{selectedPlatform}</strong></span>
                <button type="button" onClick={() => setSelectedPlatform(null)} aria-label="清除 Project 篩選">×</button>
              </div>
            ) : null}
            {selectedOwner ? (
              <div className="owner-filter-chip">
                <span>負責人: <strong>{selectedOwner}</strong></span>
                <button type="button" onClick={() => setSelectedOwner(null)} aria-label="清除負責人篩選">×</button>
              </div>
            ) : null}
            <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="篩選類別">
              <option value="全部">全部類別</option>
              {takeoverCategories.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="篩選狀態">
              <option value="全部">全部狀態</option>
              {takeoverStatuses.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </div>
        </div>
        <div className="table-wrap"><table><thead><tr><th>類別 / 項目</th><th className="working-col-owner">Owner</th><th>Project</th><th>獨立操作</th><th>文件撰寫</th><th>成熟度</th><th>狀態</th><th>備註</th>{isAdmin ? <th /> : null}</tr></thead><tbody>
          {filtered.map((row) => {
            const owners = parseOwners(row.owner)
            return (
              <tr key={row.id} className="clickable-row" onClick={() => canEdit && openEdit(row)}>
                <td>
                  <div className="item-title-wrap">
                    <button
                      type="button"
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setCategory((prev) => prev === row.category ? '全部' : row.category)
                      }}
                      title={`點擊篩選類別：${row.category}`}
                    >
                      <CategoryBadge value={row.category} />
                    </button>
                    <strong>{row.item}</strong>
                  </div>
                </td>
                <td className="working-owner-column">
                  <div className="working-owner-tags">
                    {owners.length > 0 ? owners.map((owner) => {
                      const isSelected = selectedOwner === owner
                      return (
                        <button
                          key={owner}
                          type="button"
                          className={`working-owner-chip ${isSelected ? 'active' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedOwner((prev) => (prev === owner ? null : owner))
                          }}
                          title={isSelected ? `點擊取消篩選：${owner}` : `點擊篩選負責人：${owner}`}
                        >
                          {owner}
                        </button>
                      )
                    }) : <span style={{ color: '#94a3b8' }}>—</span>}
                    {row.fii ? (
                      <span className="person-chip" style={{ margin: 0, padding: '2px 6px', fontSize: '11px' }} title={`Fii Owner: ${row.fii}`}>
                        Fii: {row.fii}
                      </span>
                    ) : null}
                  </div>
                </td>
                <td>
                  {row.platform ? (
                    <button
                      type="button"
                      className={`working-project-chip ${selectedPlatform === row.platform ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedPlatform((prev) => (prev === row.platform ? null : row.platform))
                      }}
                      title={selectedPlatform === row.platform ? `點擊取消篩選 Project：${row.platform}` : `點擊篩選 Project：${row.platform}`}
                    >
                      {row.platform}
                    </button>
                  ) : (
                    <span style={{ color: '#94a3b8' }}>—</span>
                  )}
                </td>
                <td><RateBadge value={row.independent} /></td>
                <td><RateBadge value={row.documentation} /></td>
                <td><Progress value={row.confidence} /></td>
                <td>
                  <button
                    type="button"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setStatus((prev) => prev === row.status ? '全部' : row.status)
                    }}
                    title={`點擊篩選狀態：${row.status}`}
                  >
                    <StatusBadge status={row.status} />
                  </button>
                </td>
                <td className="wide-cell takeover-notes-cell">
                  {row.note ? <span className="takeover-notes-text" title={row.note}>{row.note}</span> : <span style={{ color: '#94a3b8' }}>—</span>}
                </td>
                {isAdmin ? <td><button className="icon-button danger" aria-label="刪除" onClick={(e) => { e.stopPropagation(); void remove(row) }}><Trash size={17} /></button></td> : null}
              </tr>
            )
          })}
          {filtered.length === 0 ? <tr><td colSpan={isAdmin ? 10 : 9} className="table-empty">找不到符合條件的項目。</td></tr> : null}
        </tbody></table></div>
      </Panel>
      <Drawer open={drawerOpen} title={editing ? '編輯承接項目' : '新增承接項目'} subtitle={editing ? `版本 ${editing.version} · 最後更新 ${editing.updatedByName}` : '建立一筆新的能力盤點'} onClose={closeDrawer} footer={<><Button onClick={closeDrawer}>取消</Button><Button variant="primary" type="submit" form="takeover-form" disabled={save.isPending}>{save.isPending ? '儲存中…' : '儲存'}</Button></>}>
        <TakeoverFormView form={form} setForm={setForm} onSubmit={(e) => { e.preventDefault(); save.mutate() }} />
      </Drawer>
      <Drawer open={summaryOpen} title="編輯 Dashboard 摘要" onClose={() => setSummaryOpen(false)} footer={<><Button onClick={() => setSummaryOpen(false)}>取消</Button><Button variant="primary" onClick={() => saveSummary.mutate()} disabled={saveSummary.isPending}>儲存摘要</Button></>}>
        <div className="form-grid two"><NumberField label="掌握測試 Current" value={summary.coreItemsCurrent} onChange={(value) => setSummary({ ...summary, coreItemsCurrent: value })} /><NumberField label="掌握測試 Total" value={summary.coreItemsTotal} onChange={(value) => setSummary({ ...summary, coreItemsTotal: value })} /><NumberField label="ESS 人數" value={summary.essPeople} onChange={(value) => setSummary({ ...summary, essPeople: value })} /><NumberField label="已掌握平台 Current" value={summary.masteredPlatformsCurrent} onChange={(value) => setSummary({ ...summary, masteredPlatformsCurrent: value })} /><NumberField label="已掌握平台 Total" value={summary.masteredPlatformsTotal} onChange={(value) => setSummary({ ...summary, masteredPlatformsTotal: value })} /></div>
      </Drawer>
      <ConflictDialog open={Boolean(conflict)} local={form as unknown as Record<string, unknown>} remote={(conflict ?? {}) as unknown as Record<string, unknown>} labels={labels} onClose={() => setConflict(null)} onUseLatest={() => { if (conflict) { setEditing(conflict); setForm(toForm(conflict)); setConflict(null) } }} />
    </section>
  )
}

function TakeoverFormView({ form, setForm, onSubmit }: { form: TakeoverForm; setForm: (value: TakeoverForm) => void; onSubmit: (event: FormEvent) => void }) {
  const selectedOwners = form.owner.split(',').map((v) => v.trim()).filter(Boolean)
  const toggleOwner = (owner: string) => {
    const next = selectedOwners.includes(owner)
      ? selectedOwners.filter((v) => v !== owner)
      : [...selectedOwners, owner]
    setForm({ ...form, owner: next.join(', ') })
  }

  return (
    <form id="takeover-form" onSubmit={onSubmit} className="form-grid two">
      <Field label="類別">
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as TakeoverForm['category'] })}>
          {takeoverCategories.map((value) => <option key={value}>{value}</option>)}
        </select>
      </Field>
      <Field label="狀態">
        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as TakeoverForm['status'] })}>
          {takeoverStatuses.map((value) => <option key={value}>{value}</option>)}
        </select>
      </Field>
      <Field label="承接項目">
        <input value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} required />
      </Field>
      <Field label="Project">
        <input value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} placeholder="例如：Chervil" />
      </Field>
      <Field label="Dell Owner">
        <div className="owner-selector" role="group" aria-label="選擇 Dell Owner" style={{ marginBottom: '6px' }}>
          {workingItemOwners.map((owner) => {
            const selected = selectedOwners.includes(owner)
            return (
              <button
                key={owner}
                type="button"
                className={selected ? 'selected' : ''}
                aria-pressed={selected}
                onClick={() => toggleOwner(owner)}
              >
                {selected ? '✓' : '+'} {owner}
              </button>
            )
          })}
        </div>
        <input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} placeholder="直接輸入或點選快速選取" />
      </Field>
      <Field label="Fii Owner">
        <input value={form.fii} onChange={(e) => setForm({ ...form, fii: e.target.value })} />
      </Field>
      <NullableNumber label="總數" value={form.total} onChange={(value) => setForm({ ...form, total: value })} />
      <NullableNumber label="已測試" value={form.tested} onChange={(value) => setForm({ ...form, tested: value })} />
      <NullableNumber label="獨立完成率 (%)" value={form.independent} max={100} onChange={(value) => setForm({ ...form, independent: value })} />
      <NullableNumber label="新平台完成率 (%)" value={form.independentNew} max={100} onChange={(value) => setForm({ ...form, independentNew: value })} />
      <NullableNumber label="文件完成率 (%)" value={form.documentation} max={100} onChange={(value) => setForm({ ...form, documentation: value })} />
      <NullableNumber label="信心度 (%)" value={form.confidence} max={100} onChange={(value) => setForm({ ...form, confidence: value })} />
      <Field label="備註">
        <textarea rows={4} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
      </Field>
    </form>
  )
}

function RateBadge({ value }: { value: number | null }) {
  if (value === null) return <span style={{ color: '#94a3b8' }}>—</span>
  const tone = value >= 100 ? 'full' : value >= 70 ? 'high' : ''
  return <span className={`rate-pill ${tone}`}>{value}%</span>
}

function parseOwners(value: string) {
  return value.split(',').map((owner) => owner.trim()).filter(Boolean)
}

function NullableNumber({ label, value, max, onChange }: { label: string; value: number | null; max?: number; onChange: (value: number | null) => void }) { return <Field label={label}><input type="number" min={0} max={max} value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} /></Field> }
function NumberField({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) { return <Field label={label}><input type="number" min={0} value={value} onChange={(e) => onChange(Number(e.target.value))} /></Field> }
function Progress({ value }: { value: number | null }) { const safe = value ?? 0; return <div className="progress-cell"><div><span style={{ width: `${safe}%` }} /></div><small>{value === null ? '—' : `${value}%`}</small></div> }
function toForm(row: TakeoverItem): TakeoverForm { const { category, item, owner, fii, platform, total, tested, independent, independentNew, documentation, confidence, status, note } = row; return { category, item, owner, fii, platform, total, tested, independent, independentNew, documentation, confidence, status, note } }
function summaryValues(summary: Summary) { const { coreItemsCurrent, coreItemsTotal, essPeople, masteredPlatformsCurrent, masteredPlatformsTotal } = summary; return { coreItemsCurrent, coreItemsTotal, essPeople, masteredPlatformsCurrent, masteredPlatformsTotal } }
